import { defineStore } from 'pinia';
import { ApiError, client } from '@/api';
import { GetCurrenciesDto } from '@/api/request/currencies/get-currencies.dto';
import { CreateCurrenciesDto } from '@/api/request/currencies/create-currencies.dto';
import { UpdateCurrenciesDto } from '@/api/request/currencies/update-currencies.dto';
import { ElMessage, ElMessageBox } from 'element-plus';
import { CurrenciesDto } from '@/api/response/currencies/currencies.dto';

export type CurrenciesState = {
  currencyListMap: Record<string, string>;
  currencyList: ({label: string, value: string})[];
  currencyFlatList: string[];
  loading: boolean;
  error: string | null;
  isCreateOpened: boolean;
  isEditOpened: boolean;
  isDeleteOpened: boolean;
  selectedEntity: CurrenciesDto | null;
};

export type GroupedCurrencyListMap = Record<string, Record<string, boolean>>;

export const useCurrenciesStore = defineStore('currencies', {
  state: (): CurrenciesState => ({
    currencyListMap: {},
    currencyList: [],
    currencyFlatList: [],
    loading: false,
    error: null,
    isCreateOpened: false,
    isEditOpened: false,
    isDeleteOpened: false,
    selectedEntity: null,
  }),
  actions: {
    setLoading(value: boolean) {
      this.loading = value;
    },

    openCreate() { this.isCreateOpened = true; },
    closeCreate() { this.isCreateOpened = false; },

    openEdit(entity: CurrenciesDto) {
      this.selectedEntity = entity;
      this.isEditOpened = true;
    },
    closeEdit() { this.isEditOpened = false; },

    openDelete(entity: CurrenciesDto) {
      this.selectedEntity = entity;
      this.isDeleteOpened = true;
    },
    closeDelete() { this.isDeleteOpened = false; },

    async init() {
      await this.getCurrencyList();
    },

    isSelectedAll(selectedCurrencies: string[] = []): boolean {
      let isSelected = true;

      for (const currency of this.currencyFlatList) {
        if (!selectedCurrencies.includes(currency)) {
          isSelected = false;
          break;
        }
      }

      return isSelected;
    },

    getGroupedCurrencyListMap(selectedCurrencies: string[] = []): GroupedCurrencyListMap {
      const grouped: GroupedCurrencyListMap = {};

      for (const currency of this.currencyList) {
        if (grouped[currency.value[0]] === undefined) {
          grouped[currency.value[0]] = {};
        }

        grouped[currency.value[0]][currency.value] = selectedCurrencies.includes(currency.value);
      }

      return grouped;
    },

    async getCurrencyList() {
      if (this.currencyList.length > 0) {
        return this.currencyList;
      }

      const currencyListMap = await client.currenciesApi.getCurrencyList();
      const flatList = Object.keys(currencyListMap).sort();

      this.currencyList = flatList.map((currency: string) => ({
        label: currency,
        value: currency,
      }));

      this.currencyFlatList = flatList;
      this.currencyListMap = currencyListMap;

      return this.currencyList;
    },

    async getProvidersCurrenciesList(dto: GetCurrenciesDto) {
      this.setLoading(true);

      this.error = null;

      try {
        return await client.currenciesApi.getProvidersCurrenciesList(dto);
      } catch (err) {
        ElMessage.error('Error getting list of Providers Currencies');

        this.error = (err as any).message;

        return {
          items: [],
          total: 0,
          page: 1,
          perPage: 1,
          pageCount: 1,
        };
      } finally {
        this.setLoading(false);
      }
    },

    async createProviderCurrencies(dto: CreateCurrenciesDto) {
      this.setLoading(true);

      this.error = null;

      try {
        await client.currenciesApi.createProviderCurrencies(dto);
      } catch (err) {
        let message = 'Error during creating Providers Currencies';

        if ((err as ApiError)?.body?.message) {
          message = (err as ApiError).body.message;
        }

        ElMessage.error(message);

        this.error = message;
      } finally {
        this.setLoading(false);
      }
    },

    async updateProviderCurrencies(id: number, dto: UpdateCurrenciesDto) {
      this.setLoading(true);

      this.error = null;

      try {
        await client.currenciesApi.updateProviderCurrencies(id, dto);
      } catch (err) {
        let message = 'Error during updating Providers Currencies';

        if ((err as ApiError)?.body?.message) {
          message = (err as ApiError).body.message;
        }

        ElMessage.error(message);

        this.error = message;
      } finally {
        this.setLoading(false);
      }
    },

    async deleteProviderCurrencies(id: number) {
      try {
        await ElMessageBox.confirm('Please confirm complete deleting Provider Currencies');
      } catch (err) {
        return;
      }

      this.setLoading(true);

      this.error = null;

      try {
        await client.currenciesApi.deleteProviderCurrencies(id);
      } catch (err) {
        let message = 'Error during deleting Providers Currencies';

        if ((err as ApiError)?.body?.message) {
          message = (err as ApiError).body.message;
        }

        ElMessage.error(message);

        this.error = message;
      } finally {
        this.setLoading(false);
      }
    },
  },
});
